import React, { Children, useState } from "react";

import TabSwitcherHeader from "./tab-switcher-header";
import TabSwitcherButton from "./tab-switcher-button";

import styles from "./tab-switcher.css";

interface TabSwitcherContextType {
    currentTabIndex: number;
    setCurrentTabIndex: React.Dispatch<React.SetStateAction<number>>;
}

const TabSwitcherContext = React.createContext<TabSwitcherContextType | undefined>(undefined);

export function useTabSwitcherContext(): TabSwitcherContextType {
    const context = React.useContext(TabSwitcherContext);

    if (!context) {
        throw new Error(`useTabSwitcherContext must be used within a TabSwitcherContext.Provider`);
    }

    return context;
}

interface TabSwitcherProps {
    children: typeof TabSwitcherHeader | React.ReactChild | React.ReactFragment | React.ReactPortal;
    initialTabIndex?: number;
}

function TabSwitcher({ children, initialTabIndex }: TabSwitcherProps): React.ReactElement {
    const [currentTabIndex, setCurrentTabIndex] = useState(initialTabIndex || 0);
    const childrenArr = Children.toArray(children);

    const tabs = children[0];
    const content = childrenArr[currentTabIndex + 1];

    if (!tabs || tabs.type !== TabSwitcherHeader) {
        throw "You must provide TabSwitcherHeader as the first child";
    }

    if (tabs.props.children.length !== childrenArr.length - 1) {
        throw "You must provide equal number of tabs and child components";
    }

    return (
        <TabSwitcherContext.Provider value={{ currentTabIndex, setCurrentTabIndex }}>
            {tabs}

            <div
                className={styles.tabContent}
                id={`tabpanel-${currentTabIndex}`}
                aria-labelledby={`tabpanel-${currentTabIndex}`}
                tabIndex={0}
                role="tabpanel"
            >
                {content}
            </div>
        </TabSwitcherContext.Provider>
    );
}

TabSwitcher.Header = TabSwitcherHeader;
TabSwitcher.Tab = TabSwitcherButton;

export default TabSwitcher;
